import { ToucanComponents } from '@jointoucan/toucan-design';
import { Alert, AlertTitle } from '@mui/material';

import { useGetPermissionsQuery, useRequestSitePermissionsMutation } from '~/extension/schema';
import { trackEvent } from '~/helpers/analytics';
import { useExtension } from '~/hooks/useExtension';
import { useFirefoxExtension } from '~/hooks/useFirefoxExtension';

import { DoctorCard } from './DoctorCard';

const title = 'Permission Info';
enum Label {
  ToucanSitePermissions = 'Toucan Site Permissions',
  GlobalPermissions = 'Global Permissions',
}

const { Button } = ToucanComponents;

const StandardPermissionInfo = () => {
  const { isInstalled, hasGlobalPermissions, loading } = useExtension();
  const { data, error, refetch, loading: permissionsLoading } = useGetPermissionsQuery({
    // Should we use Config.SITE_URL instead of the hardcoded value below?
    variables: { domain: 'https://jointoucan.com/*' },
    skip: !isInstalled,
  });
  const [requestPermissions] = useRequestSitePermissionsMutation();
  const hasToucanSitePermissions = data?.browser?.permissions;

  if (!isInstalled || loading || permissionsLoading) {
    return null;
  }
  return (
    <DoctorCard
      title={title}
      beforeContent={
        error && (
          <Alert severity="error">
            <AlertTitle>We ran into some issues</AlertTitle>
            {error.message}
          </Alert>
        )
      }
      asideContent={
        !hasToucanSitePermissions && (
          <Button
            size="small"
            onClick={async () => {
              try {
                trackEvent({ name: 'Doctor fix permissions' });
                await requestPermissions({ domain: 'https://jointoucan.com/*' });
                await refetch();
              } catch (e) {
                // Ignore error
              }
            }}
          >
            Fix
          </Button>
        )
      }
      rows={[
        {
          label: Label.ToucanSitePermissions,
          value: hasToucanSitePermissions ?? false,
        },
        {
          label: Label.GlobalPermissions,
          value: hasGlobalPermissions,
        },
      ]}
    />
  );
};

const FirefoxFallbackPermissionInfo = () => {
  const { hasGlobalPermissionsInFirefox, isInstalledInFirefox, loading } = useFirefoxExtension();
  if (!isInstalledInFirefox || loading) {
    return null;
  }
  return (
    <DoctorCard
      title={title}
      rows={[
        /**
         * If I understand correctly, in Firefox, there is currently no way
         * to revoke access to jointoucan.com after installing the extension.
         * For that reason, I'm omitting the 'Toucan Site Permissions' row.
         * However, we may need to add it once Firefox updates their host
         * permissions policy to match the policy Chrome is rolling out
         * alongside MV3.
         */
        {
          label: Label.GlobalPermissions,
          value: hasGlobalPermissionsInFirefox,
        },
      ]}
    />
  );
};

export const PermissionInfo = () => {
  const { shouldFallBack } = useExtension();
  return shouldFallBack ? <FirefoxFallbackPermissionInfo /> : <StandardPermissionInfo />;
};
